import type { WorkspaceNoteTag } from '../services/notesV2Api';

/**
 * Client-side helpers for reusing a workspace's existing note tags.
 *
 * Tags are freetext, and the workspace vocabulary is derived from the tags
 * already in use rather than from a registry. These helpers exist so that
 * offering a suggestion actually keeps the vocabulary from fragmenting —
 * without them, autocomplete could show "TABOR" while someone typing "tabor"
 * quietly created a second spelling of the same tag.
 *
 * Identity is case-insensitive throughout, matching how the notes API filters
 * tags. Nothing here rewrites a stored tag; it only decides what a new one is
 * called.
 */

/** True when two tags are the same tag, ignoring how they were capitalized. */
export function isSameTag(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Resolve what a newly typed tag should be called.
 *
 * If the workspace already uses this tag under some capitalization, return
 * that spelling so the two don't diverge. Otherwise return the input trimmed —
 * a genuinely new tag is spelled however its author spelled it.
 */
export function canonicalizeTag(typed: string, vocabulary: WorkspaceNoteTag[]): string {
  const trimmed = typed.trim();
  if (!trimmed) return '';
  return vocabulary.find((t) => t.key === trimmed.toLowerCase())?.label ?? trimmed;
}

/**
 * The suggestions to offer beneath the tag input: vocabulary the note does not
 * already carry, narrowed by whatever has been typed so far.
 *
 * @param limit - keep the list short enough to scan at a glance
 */
export function filterTagSuggestions(
  vocabulary: WorkspaceNoteTag[],
  tagsOnNote: string[],
  typed: string,
  limit = 8
): WorkspaceNoteTag[] {
  const onNote = new Set(tagsOnNote.map((t) => t.trim().toLowerCase()));
  const needle = typed.trim().toLowerCase();

  return vocabulary
    .filter((t) => !onNote.has(t.key))
    .filter((t) => !needle || t.key.includes(needle))
    .slice(0, limit);
}

/**
 * Add a tag to a note's list, reusing the workspace spelling when there is one
 * and ignoring a tag the note already carries under different capitalization.
 * Returns the list unchanged when there is nothing to add.
 */
export function addTagToList(
  tagsOnNote: string[],
  typed: string,
  vocabulary: WorkspaceNoteTag[]
): string[] {
  const canonical = canonicalizeTag(typed, vocabulary);
  if (!canonical) return tagsOnNote;
  if (tagsOnNote.some((t) => isSameTag(t, canonical))) return tagsOnNote;
  return [...tagsOnNote, canonical];
}
